/**
 * PRP-WEB-2: undo/redo over the plan AND the subroom groups. The core's `History` snapshots the
 * plan only; groups live outside room geometry (DD1), so a grouping step is still one undo
 * step here. Every plan checkpoint also records the groups as they were; a groups-only edit
 * checkpoints the (unchanged) plan. Drop-in for `History` (`runEdit` takes it).
 */
import { History, type Plan } from '@sketch-core';
import type { PlanGroup } from '../../../../lib/planApi';

const js = (g: PlanGroup[]) => JSON.stringify(g);

export class EditHistory extends History {
  /** The groups as they are now; the page keeps it current before every step. */
  groups: PlanGroup[] = [];
  private gUndo: string[] = [];
  private gRedo: string[] = [];

  private pushG(): void {
    this.gUndo.push(js(this.groups));
    while (this.gUndo.length > this.undoStack.length) this.gUndo.shift();
    this.gRedo = [];
  }

  checkpoint(plan: Plan): void {
    super.checkpoint(plan);
    this.pushG();
  }

  run<T extends { ok: boolean }>(plan: Plan, action: (p: Plan) => T): T {
    const res = super.run(plan, action);
    if (res && res.ok) this.pushG();
    return res;
  }

  discard(): void {
    super.discard();
    const g = this.gUndo.pop();
    if (g != null) this.groups = JSON.parse(g) as PlanGroup[];
  }

  undo(current: Plan): Plan | null {
    const p = super.undo(current);
    if (!p) return null;
    this.gRedo.push(js(this.groups));
    const g = this.gUndo.pop();
    if (g != null) this.groups = JSON.parse(g) as PlanGroup[];
    return p;
  }

  redo(current: Plan): Plan | null {
    const p = super.redo(current);
    if (!p) return null;
    this.gUndo.push(js(this.groups));
    const g = this.gRedo.pop();
    if (g != null) this.groups = JSON.parse(g) as PlanGroup[];
    return p;
  }
}
